import { createBrowserRouter, Navigate } from 'react-router-dom'

import { AppShell } from './layout/AppShell'
import { RequireStaff } from '@/features/auth/RequireStaff'
import { NotFoundPage } from './NotFoundPage'
import { SettingsLayout, BusinessSettingsPage } from '@/features/settings/SettingsPage'
import { AccountSettingsPage } from '@/features/settings/AccountSettingsPage'
import { ThemeSettingsPage } from '@/features/settings/ThemeSettingsPage'
import { TemplatesSettingsPage } from '@/features/settings/TemplatesSettingsPage'
import { FeedPlannerPage } from '@/features/feed/FeedPlannerPage'
import { MarketingDashboardPage } from '@/features/marketing/MarketingDashboardPage'
import { CampaignsPage } from '@/features/marketing/CampaignsPage'
import { PipelinePage } from '@/features/pipeline/PipelinePage'
import { JobDetailPage } from '@/features/pipeline/JobDetailPage'
import { NewWeddingPage } from '@/features/pipeline/NewWeddingPage'
import { EnquiriesPage } from '@/features/inquiries/EnquiriesPage'
import { EnquiryDetailPage } from '@/features/inquiries/EnquiryDetailPage'
import { NewEnquiryPage } from '@/features/inquiries/NewEnquiryPage'
import { VenuesPage } from '@/features/venues/VenuesPage'
import { TasksPage } from '@/features/tasks/TasksPage'
import { QuoteEditorPage } from '@/features/quotes/QuoteEditorPage'
import { QuotesPage } from '@/features/quotes/QuotesPage'
import { ContractPage, ContractsPage } from '@/features/contracts/ContractsPage'
import { Forward } from './Forward'
import { InvoicePage } from '@/features/invoices/InvoicePage'
import { InvoicesPage } from '@/features/invoices/InvoicesPage'
import { PaymentPlansPage } from '@/features/invoices/PaymentPlansPage'
import { TeamSettingsPage } from '@/features/team/TeamSettingsPage'
import { EditingPage } from '@/features/editing/EditingPage'
import { CommunicationsPage } from '@/features/communications/CommunicationsPage'
import { GalleryPage } from '@/features/galleries/GalleryPage'
import { GalleriesPage } from '@/features/galleries/GalleriesPage'
import { CrewPage } from '@/features/team/CrewPage'
import { ClientPortalPage } from '@/features/portal/ClientPortalPage'
import { TeamPage } from '@/features/team/TeamPage'
import { TeamMemberPage } from '@/features/team/TeamMemberPage'
import { HqPage, PersonalPage } from '@/features/hq/HqPage'
import { VendorsPage } from '@/features/vendors/VendorsPage'
import { CalendarPage } from '@/features/calendar/CalendarPage'
import { ContactsPage } from '@/features/contacts/ContactsPage'
import { QuestionnairesPage } from '@/features/questionnaires/QuestionnairesPage'
import { QuestionnaireDetailPage } from '@/features/questionnaires/QuestionnaireDetailPage'
import { QuestionnaireTemplatePage } from '@/features/questionnaires/QuestionnaireTemplatePage'
import { QuestionnaireFillPage } from '@/features/questionnaires/QuestionnaireFillPage'

/**
 * Routes.
 *
 * Two trees will eventually hang off the root: the internal studio behind
 * RequireStaff, and the client portal at /portal with its own layout and its
 * own guard. Only the internal tree exists so far.
 *
 * `/weddings` rather than `/jobs` in the URL. The database calls them jobs
 * because that is what the brief calls them and what a practice management
 * system means, but every URL she sees and every heading says wedding.
 */
export const router = createBrowserRouter([
  // A couple's gallery: public, its own calm page, no staff chrome or login.
  { path: '/g/:slug', element: <GalleryPage /> },
  { path: '/crew/:token', element: <CrewPage /> },
  { path: '/q/:token', element: <QuestionnaireFillPage /> },
  // The couple's portal: public, the private link is the key (28 Oct 2026).
  { path: '/p/:token', element: <ClientPortalPage /> },

  {
    path: '/',
    element: (
      <RequireStaff>
        <AppShell />
      </RequireStaff>
    ),
    children: [
      // To do is the morning screen: her lists for today, the week and herself.
      { index: true, element: <Navigate to="/todo" replace /> },
      { path: 'todo', element: <HqPage /> },
      { path: 'hq', element: <Navigate to="/todo" replace /> },
      { path: 'personal', element: <PersonalPage /> },

      { path: 'projects', element: <PipelinePage /> },
      { path: 'pipeline', element: <Navigate to="/projects" replace /> },
      { path: 'calendar', element: <CalendarPage /> },

      { path: 'weddings/new', element: <NewWeddingPage /> },
      { path: 'weddings/:id', element: <JobDetailPage /> },

      // Documents belong to a client, with the project optional (29 Oct 2026),
      // so they have addresses of their own. The old per-wedding addresses
      // forward: "new" opens the New … form with that project filled in.
      { path: 'weddings/:jobId/quotes/:quoteId', element: <Forward to="quotes" param="quoteId" /> },
      { path: 'weddings/:jobId/invoices/:invoiceId', element: <Forward to="invoices" param="invoiceId" /> },
      { path: 'quotes', element: <QuotesPage /> },
      { path: 'quotes/:quoteId', element: <QuoteEditorPage /> },
      { path: 'invoices', element: <InvoicesPage /> },
      { path: 'invoices/:invoiceId', element: <InvoicePage /> },
      { path: 'contracts', element: <ContractsPage /> },
      { path: 'contracts/:contractId', element: <ContractPage /> },
      { path: 'questionnaires', element: <QuestionnairesPage /> },
      { path: 'questionnaires/:id', element: <QuestionnaireDetailPage /> },

      { path: 'enquiries', element: <EnquiriesPage /> },
      { path: 'enquiries/new', element: <NewEnquiryPage /> },
      { path: 'enquiries/:id', element: <EnquiryDetailPage /> },

      { path: 'tasks', element: <TasksPage /> },
      { path: 'editing', element: <EditingPage /> },
      { path: 'feed', element: <FeedPlannerPage /> },
      { path: 'marketing', element: <MarketingDashboardPage /> },
      { path: 'marketing/campaigns', element: <CampaignsPage /> },
      { path: 'communications', element: <CommunicationsPage /> },
      { path: 'galleries', element: <GalleriesPage /> },
      { path: 'venues', element: <Navigate to="/settings/venues" replace /> },
      // Team and vendors are tabs in Settings since 28 Oct 2026; a person's
      // own page keeps its short address.
      { path: 'team', element: <Navigate to="/settings/team" replace /> },
      { path: 'team/:id', element: <TeamMemberPage /> },
      { path: 'vendors', element: <Navigate to="/settings/vendors" replace /> },
      { path: 'contacts', element: <ContactsPage /> },

      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <BusinessSettingsPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'vendors', element: <VendorsPage /> },
          { path: 'crew-terms', element: <TeamSettingsPage /> },
          { path: 'payment-plans', element: <PaymentPlansPage /> },
          { path: 'venues', element: <VenuesPage /> },
          { path: 'templates', element: <TemplatesSettingsPage /> },
          { path: 'templates/questionnaires/:id', element: <QuestionnaireTemplatePage /> },
          { path: 'theme', element: <ThemeSettingsPage /> },
          { path: 'account', element: <AccountSettingsPage /> },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
